import { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { User, Edit, Save, X, Package, FileText, Search, LogOut, MapPin, Lock, Eye, Truck, MapPinIcon, ChevronLeft, Heart, Building2 } from 'lucide-react';
import { Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbPage, BreadcrumbSeparator } from '@/components/ui/breadcrumb';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Separator } from '@/components/ui/separator';
import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useAuth } from '@/contexts/AuthContext';
import { useFavorites } from '@/contexts/FavoritesContext';
import { supabase } from '@/integrations/supabase/client';
import AddressBook from '@/components/AddressBook';
import ProductCard from '@/components/ProductCard';
import { toast } from 'sonner';


interface Order {
  id: string;
  order_number: string;
  status?: string | null;
  total_amount: number;
  total_delivery_cost: number;
  created_at: string;
  billing_address?: any;
  shipping_city?: string;
  shipping_option?: string;
  full_shipping_address?: {
    recipient_name: string;
    street_name: string;
    street_number: string;
    city: string;
    details?: string | null;
  } | null;
  sub_orders: {
    id: string;
    quantity: number;
    unit_price: number;
    total_price: number;
    delivery_cost: number;
    status: string;
    delivery_option: string;
    delivery_address: string | null;
    pickup_address: string | null;
    notes: string | null;
    product_id?: string;
    product_name?: string;
    products: {
      name: string;
      supplier_name: string;
      images?: string[];
      main_image_index?: number;
      slug?: string | null;
      measurement_unit?: string;
      package_coverage_per_unit?: number;
      liquidation_sale_enabled?: boolean;
      group_buy_enabled?: boolean;
    };
  }[];
}


const Profile = () => {
  const { user, profile, signOut, updateProfile, changePassword } = useAuth();
  const { favorites } = useFavorites();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [orders, setOrders] = useState<Order[]>([]);
  const [favoriteProducts, setFavoriteProducts] = useState<any[]>([]);
  const [favoritesLoading, setFavoritesLoading] = useState(false);
  const [showPasswordChange, setShowPasswordChange] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
  const [orderModalOpen, setOrderModalOpen] = useState(false);
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [passwordLoading, setPasswordLoading] = useState(false);

  const [formData, setFormData] = useState({
    first_name: profile?.first_name || '',
    last_name: profile?.last_name || '',
    phone: profile?.phone || '',
    tax_id: (profile as any)?.tax_id || '',
    business_name: (profile as any)?.business_name || '',
    business_address: (profile as any)?.business_address || '',
  });

  // Get active tab from URL params - default to 'orders'
  const activeTab = searchParams.get('tab') || 'orders';

  const isDirty = useMemo(() => {
    if (!profile) return false;
    return (
      formData.first_name !== (profile.first_name || '') ||
      formData.last_name !== (profile.last_name || '') ||
      formData.phone !== (profile.phone || '') ||
      formData.tax_id !== ((profile as any)?.tax_id || '') ||
      formData.business_name !== ((profile as any)?.business_name || '') ||
      formData.business_address !== ((profile as any)?.business_address || '')
    );
  }, [formData, profile]);

  useEffect(() => {
    if (!user) {
      navigate('/');
      return;
    }

    if (profile) {
      setFormData({
        first_name: profile.first_name || '',
        last_name: profile.last_name || '',
        phone: profile.phone || '',
        tax_id: (profile as any)?.tax_id || '',
        business_name: (profile as any)?.business_name || '',
        business_address: (profile as any)?.business_address || '',
      });
    }

    fetchUserData();
  }, [user, profile, navigate]);

  // Fetch favorite products when favorites change or tab becomes active
  useEffect(() => {
    if (activeTab === 'favorites' && favorites.size > 0) {
      fetchFavoriteProducts();
    }
  }, [activeTab, favorites]);

  const fetchFavoriteProducts = async () => {
    if (favorites.size === 0) {
      setFavoriteProducts([]);
      return;
    }

    setFavoritesLoading(true);
    try {
      const favoriteIds = Array.from(favorites);

      if (favoriteIds.length === 0) {
        setFavoriteProducts([]);
        setFavoritesLoading(false);
        return;
      }

      // Use secure function to fetch products
      const { data: products, error } = await supabase
        .rpc('get_products_by_ids', { product_ids: favoriteIds });

      if (error) throw error;

      // Fetch categories and subcategories separately
      const categoryIds = products?.map(p => p.category_id).filter(Boolean) || [];
      const subcategoryIds = products?.map(p => p.subcategory_id).filter(Boolean) || [];

      const [categoriesResult, subcategoriesResult] = await Promise.all([
        categoryIds.length > 0 ? supabase.from('categories').select('id, name').in('id', categoryIds) : { data: [] },
        subcategoryIds.length > 0 ? supabase.from('subcategories').select('id, name').in('id', subcategoryIds) : { data: [] }
      ]);

      // Map categories and subcategories to products
      const categoriesMap = new Map((categoriesResult.data || []).map(c => [c.id, c]));
      const subcategoriesMap = new Map((subcategoriesResult.data || []).map(s => [s.id, s]));

      const enhancedProducts = products?.map(product => ({
        ...product,
        categories: product.category_id ? categoriesMap.get(product.category_id) : null,
        subcategories: product.subcategory_id ? subcategoriesMap.get(product.subcategory_id) : null
      })) || [];

      setFavoriteProducts(enhancedProducts);
    } catch (error) {
      console.error('Error fetching favorite products:', error);
      toast.error('שגיאה בטעינת המוצרים המועדפים');
    } finally {
      setFavoritesLoading(false);
    }
  };

  const fetchUserData = async () => {
    if (!user) return;

    try {
      const ordersResult = await supabase
        .from('orders')
        .select(`
          id,
          order_number,
          status,
          total_amount,
          total_delivery_cost,
          created_at,
          shipping_option,
          shipping_zone,
          shipping_city,
          billing_address,
          sub_orders (
            id,
            quantity,
            unit_price,
            total_price,
            status,
            delivery_option,
            delivery_address,
            pickup_address,
            product_name,
            product_id
          )
        `)
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      // Fetch user's addresses for full address display
      const { data: userAddresses } = await supabase
        .from('addresses')
        .select('*')
        .eq('user_id', user.id);

      if (ordersResult.data) {
        // Fetch product details including images
        const productIds = ordersResult.data.flatMap(order =>
          order.sub_orders.map(sub => sub.product_id).filter(Boolean)
        );

        let productsMap = new Map();
        if (productIds.length > 0) {
          const { data: products } = await supabase
            .rpc('get_products_by_ids', { product_ids: productIds });

          if (products) {
            productsMap = new Map(products.map(p => [p.id, p]));
          }
        }

        // Transform data to match Order interface with full addresses
        const transformedOrders = ordersResult.data.map(order => {
          // Find most recently used address or first address for this user
          const mostRecentAddress = userAddresses?.sort((a, b) => {
            if (!a.last_used_at && !b.last_used_at) return 0;
            if (!a.last_used_at) return 1;
            if (!b.last_used_at) return -1;
            return new Date(b.last_used_at).getTime() - new Date(a.last_used_at).getTime();
          })[0];

          return {
            ...order,
            // Add full address info to order
            full_shipping_address: order.shipping_city && mostRecentAddress ? {
              recipient_name: mostRecentAddress.recipient_name,
              street_name: mostRecentAddress.street_name,
              street_number: mostRecentAddress.street_number,
              city: mostRecentAddress.city,
              details: mostRecentAddress.details
            } : null,
            sub_orders: order.sub_orders.map(subOrder => {
              const product = productsMap.get(subOrder.product_id);
              // Use actual delivery_option from sub_orders, fallback to order shipping_option
              const effectiveDeliveryOption =
                subOrder.delivery_option ||
                order.shipping_option ||
                (order.total_delivery_cost === 0 ? 'pickup' : 'delivery');

              return {
                ...subOrder,
                delivery_cost: 0,
                delivery_option: effectiveDeliveryOption,
                notes: null,
                products: {
                  name: subOrder.product_name || 'מוצר לא זמין',
                  supplier_name: '',
                  images: product?.images || [],
                  main_image_index: product?.main_image_index || 0,
                  slug: product?.slug || null,
                  measurement_unit: product?.measurement_unit,
                  package_coverage_per_unit: product?.package_coverage_per_unit,
                  liquidation_sale_enabled: product?.liquidation_sale_enabled,
                  group_buy_enabled: product?.group_buy_enabled,
                }
              };
            })
          };
        });
        setOrders(transformedOrders as Order[]);
      }
    } catch (error) {
      console.error('Error fetching user data:', error);
    }
  };

  // Format order number for display - 9 digits with dashes: XXX-XXX-XXX
  const formatOrderNumber = (orderNumber: string) => {
    if (!orderNumber) return 'לא זמין';
    // Remove all non-digits
    const digits = orderNumber.replace(/[^0-9]/g, '');
    // Format as XXX-XXX-XXX if we have 9 digits
    if (digits.length === 9) {
      return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6, 9)}`;
    }
    // Return as-is if not 9 digits
    return orderNumber;
  };

  const handleOrderClick = (order: Order) => {
    setSelectedOrder(order);
    setOrderModalOpen(true);

    // Track view_order event
    if (typeof window !== 'undefined' && window.gtag) {
      window.gtag('event', 'view_order', {
        order_id: order.id,
        value: order.total_amount,
        currency: 'ILS'
      });
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      // Simple Israeli phone validation
      if (formData.phone && formData.phone.trim()) {
        const { validateIsraeliPhone } = await import('@/lib/phoneValidation');
        if (!validateIsraeliPhone(formData.phone)) {
          toast.error('מספר טלפון לא תקין. נדרש מספר המתחיל ב-05 ובאורך 10 ספרות.');
          setLoading(false);
          return;
        }
      }

      await updateProfile({
        first_name: formData.first_name,
        last_name: formData.last_name,
        phone: formData.phone,
        tax_id: formData.tax_id || null,
        business_name: formData.business_name || null,
        business_address: formData.business_address || null,
      });

      setIsEditing(false);
      // Toast message is handled in AuthContext
    } catch (error) {
      console.error('Error updating profile:', error);
      toast.error('שגיאה בעדכון הפרופיל');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setFormData({
      first_name: profile?.first_name || '',
      last_name: profile?.last_name || '',
      phone: profile?.phone || '',
      tax_id: profile?.tax_id || '',
      business_name: profile?.business_name || '',
      business_address: profile?.business_address || '',
    });
    setIsEditing(false);
  };

  const handlePasswordChange = async () => {
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      toast.error('הסיסמאות אינן תואמות');
      return;
    }

    if (!passwordData.currentPassword) {
      toast.error('יש להזין את הסיסמה הנוכחית');
      return;
    }

    setPasswordLoading(true);
    try {
      await changePassword(passwordData.currentPassword, passwordData.newPassword);
      toast.success('הסיסמה עודכנה בהצלחה');
      setShowPasswordChange(false);
      setPasswordData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
      });
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'שגיאה בעדכון הסיסמה');
    } finally {
      setPasswordLoading(false);
    }
  };

  const getStatusBadge = (status: string) => {
    const statusMap = {
      pending: { label: 'ממתין לתשלום', color: '#6B7280' },
      paid: { label: 'שולם', color: '#6366F1' },
      processing: { label: 'בטיפול', color: '#22C55E' },
      ready_for_pickup: { label: 'מוכן לאיסוף', color: '#F59E0B' },
      shipped: { label: 'נשלח', color: '#0EA5E9' },
      completed: { label: 'הושלם', color: '#059669' },
      cancelled: { label: 'בוטל', color: '#DC2626' },
    };

    const statusInfo = statusMap[status as keyof typeof statusMap] || { label: status, color: '#6B7280' };
    return (
      <Badge
        variant="secondary"
        style={{
          backgroundColor: statusInfo.color,
          color: 'white',
          border: 'none'
        }}
      >
        {statusInfo.label}
      </Badge>
    );
  };

  const getDeliveryOptionLabel = (option: string) => {
    const optionMap = {
      pickup_only: 'איסוף עצמי',
      pickup: 'איסוף עצמי',
      delivery_only: 'משלוח',
      delivery: 'משלוח',
      both: 'איסוף או משלוח'
    };
    return optionMap[option as keyof typeof optionMap] || option;
  };

  if (!user || !profile) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-xl font-semibold mb-2">אין הרשאה</h2>
          <p className="text-muted-foreground">יש להתחבר כדי לצפות בעמוד זה</p>
          <Button onClick={() => navigate('/')} className="mt-4">
            חזור לעמוד הראשי
          </Button>
        </div>
      </div>
    );
  }

  return (
    <>
      <Navigation />
      <div className="min-h-screen bg-background pt-4 lg:pt-8" dir="rtl">
        <div className="container mx-auto px-4 py-2">
          {/* Breadcrumbs */}
          <div className="mb-6 mt-0" dir="rtl">
            <Breadcrumb>
              <BreadcrumbList>
                <BreadcrumbItem>
                  <BreadcrumbLink href="/">
                    בית
                  </BreadcrumbLink>
                </BreadcrumbItem>
                <BreadcrumbSeparator>
                  <ChevronLeft className="h-4 w-4" />
                </BreadcrumbSeparator>
                <BreadcrumbItem>
                  <BreadcrumbPage>פרופיל</BreadcrumbPage>
                </BreadcrumbItem>
              </BreadcrumbList>
            </Breadcrumb>
          </div>

          {/* Header */}
          <div className="flex justify-between items-center mb-8">
            <div className="text-right">
              <h1 className="text-3xl font-bold">חשבון שלי</h1>
              <p className="text-muted-foreground mt-2">
                ברוך הבא, {profile.first_name} {profile.last_name}
              </p>
            </div>

            <Button onClick={signOut} variant="outline" className="flex-row-reverse">
              <LogOut className="w-4 h-4 mr-2" />
              התנתק
            </Button>
          </div>

          <Tabs value={activeTab} onValueChange={(value) => navigate(`/profile?tab=${value}`)} className="w-full" dir="rtl">
            <TabsList className="flex w-full overflow-x-auto gap-2 bg-transparent scrollbar-hide mb-8 h-auto md:grid md:grid-cols-4 md:bg-muted md:gap-0 p-1" dir="rtl">
              <TabsTrigger value="orders" className="flex-shrink-0 rounded-full border border-border bg-background px-4 py-2 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground md:rounded-sm md:border-0 md:bg-transparent md:px-3 md:py-1.5 md:data-[state=active]:bg-background md:data-[state=active]:text-foreground md:data-[state=active]:shadow-sm">הזמנות</TabsTrigger>
              <TabsTrigger value="profile" className="flex-shrink-0 rounded-full border border-border bg-background px-4 py-2 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground md:rounded-sm md:border-0 md:bg-transparent md:px-3 md:py-1.5 md:data-[state=active]:bg-background md:data-[state=active]:text-foreground md:data-[state=active]:shadow-sm">פרטים אישיים</TabsTrigger>
              <TabsTrigger value="addresses" className="flex-shrink-0 rounded-full border border-border bg-background px-4 py-2 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground md:rounded-sm md:border-0 md:bg-transparent md:px-3 md:py-1.5 md:data-[state=active]:bg-background md:data-[state=active]:text-foreground md:data-[state=active]:shadow-sm">כתובות</TabsTrigger>
              <TabsTrigger value="favorites" className="flex-shrink-0 rounded-full border border-border bg-background px-4 py-2 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground md:rounded-sm md:border-0 md:bg-transparent md:px-3 md:py-1.5 md:data-[state=active]:bg-background md:data-[state=active]:text-foreground md:data-[state=active]:shadow-sm">מועדפים</TabsTrigger>
            </TabsList>

            {/* Profile Tab */}
            <TabsContent value="profile" dir="rtl" className="text-right">
              <Card dir="rtl" className="text-right">
                <CardHeader className="flex flex-col sm:flex-row items-start sm:items-center justify-between text-right gap-4" dir="rtl">
                  <CardTitle className="flex items-center gap-2 text-right flex-row-reverse">
                    <User className="h-5 w-5" />
                    פרטים אישיים
                  </CardTitle>
                  {!isEditing ? (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setIsEditing(true)}
                      className="flex-row-reverse"
                    >
                      <Edit className="h-4 w-4 mr-2" />
                      עריכה
                    </Button>
                  ) : isDirty ? (
                    <div className="flex gap-2 flex-row-reverse mt-4 sm:mt-0">
                      <Button
                        size="sm"
                        onClick={handleSave}
                        disabled={loading}
                        className="flex-row-reverse"
                      >
                        <Save className="h-4 w-4 mr-2" />
                        שמור
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleCancel}
                        className="flex-row-reverse"
                      >
                        <X className="h-4 w-4 mr-2" />
                        ביטול
                      </Button>
                    </div>
                  ) : null}
                </CardHeader>
                <CardContent className="space-y-4 text-right" dir="rtl">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4" dir="rtl">
                    <div className="space-y-2">
                      <Label htmlFor="first_name" className="block text-right" style={{ textAlign: 'right' }}>שם פרטי</Label>
                      <Input
                        id="first_name"
                        name="first_name"
                        value={formData.first_name}
                        onChange={handleInputChange}
                        disabled={!isEditing}
                        className="text-right [direction:rtl] [text-align:right]"
                        dir="rtl"
                        style={{ textAlign: 'right', direction: 'rtl' }}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="last_name" className="block text-right" style={{ textAlign: 'right' }}>שם משפחה</Label>
                      <Input
                        id="last_name"
                        name="last_name"
                        value={formData.last_name}
                        onChange={handleInputChange}
                        disabled={!isEditing}
                        className="text-right [direction:rtl] [text-align:right]"
                        dir="rtl"
                        style={{ textAlign: 'right', direction: 'rtl' }}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="email" className="block text-right" style={{ textAlign: 'right' }}>אימייל</Label>
                      <Input
                        id="email"
                        value={profile.email}
                        disabled
                        className="text-right bg-muted [direction:rtl] [text-align:right]"
                        dir="rtl"
                        style={{ textAlign: 'right', direction: 'rtl' }}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="phone" className="block text-right" style={{ textAlign: 'right' }}>טלפון</Label>
                      <Input
                        id="phone"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        disabled={!isEditing}
                        className="text-right [direction:rtl] [text-align:right] placeholder:text-muted-foreground/40 placeholder:text-xs"
                        placeholder="05x-xxxxxxx"
                        dir="rtl"
                        style={{ textAlign: 'right', direction: 'rtl' }}
                      />
                    </div>
                  </div>

                  {/* Business Details Section */}
                  <div className="mt-6 pt-6 border-t">
                    <h3 className="text-lg font-medium mb-4 text-right">פרטי עסק (אופציונלי)</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4" dir="rtl">
                      <div className="space-y-2">
                        <Label htmlFor="tax_id" className="block text-right" style={{ textAlign: 'right' }}>ח.פ.</Label>
                        <Input
                          id="tax_id"
                          name="tax_id"
                          value={formData.tax_id}
                          onChange={handleInputChange}
                          disabled={!isEditing}
                          placeholder="מספר עוסק מורשה / ח.פ."
                          className="text-right [direction:rtl] [text-align:right] placeholder:text-muted-foreground/40 placeholder:text-xs"
                          dir="rtl"
                          style={{ textAlign: 'right', direction: 'rtl' }}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="business_name" className="block text-right" style={{ textAlign: 'right' }}>שם עסק</Label>
                        <Input
                          id="business_name"
                          name="business_name"
                          value={formData.business_name}
                          onChange={handleInputChange}
                          disabled={!isEditing}
                          placeholder="שם העסק"
                          className="text-right [direction:rtl] [text-align:right] placeholder:text-muted-foreground/40 placeholder:text-xs"
                          dir="rtl"
                          style={{ textAlign: 'right', direction: 'rtl' }}
                        />
                      </div>
                      <div className="space-y-2 md:col-span-2">
                        <Label htmlFor="business_address" className="block text-right" style={{ textAlign: 'right' }}>כתובת עסק</Label>
                        <Input
                          id="business_address"
                          name="business_address"
                          value={formData.business_address}
                          onChange={handleInputChange}
                          disabled={!isEditing}
                          placeholder="כתובת העסק המלאה"
                          className="text-right [direction:rtl] [text-align:right] placeholder:text-muted-foreground/40 placeholder:text-xs"
                          dir="rtl"
                          style={{ textAlign: 'right', direction: 'rtl' }}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Password Change Section */}
                  <div className="mt-6 pt-6 border-t text-right">
                    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4 text-right gap-4">
                      <h3 className="text-lg font-medium flex items-center gap-2 flex-row-reverse text-right">
                        <Lock className="h-5 w-5" />
                        שינוי סיסמה
                      </h3>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setShowPasswordChange(!showPasswordChange)}
                        className="text-right"
                      >
                        {showPasswordChange ? 'ביטול' : 'שנה סיסמה'}
                      </Button>
                    </div>

                    {showPasswordChange && (
                      <div className="space-y-4 text-right">
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          <div className="space-y-2">
                            <Label htmlFor="currentPassword" className="block text-right" style={{ textAlign: 'right' }}>סיסמה נוכחית</Label>
                            <Input
                              id="currentPassword"
                              type="password"
                              value={passwordData.currentPassword}
                              onChange={(e) => setPasswordData(prev => ({ ...prev, currentPassword: e.target.value }))}
                              className="text-right [direction:rtl] [text-align:right]"
                              dir="rtl"
                              placeholder="הזן סיסמה נוכחית"
                              style={{ textAlign: 'right', direction: 'rtl' }}
                              required
                            />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="newPassword" className="block text-right" style={{ textAlign: 'right' }}>סיסמה חדשה</Label>
                            <Input
                              id="newPassword"
                              type="password"
                              value={passwordData.newPassword}
                              onChange={(e) => setPasswordData(prev => ({ ...prev, newPassword: e.target.value }))}
                              className="text-right [direction:rtl] [text-align:right]"
                              dir="rtl"
                              placeholder="לפחות 6 תווים"
                              style={{ textAlign: 'right', direction: 'rtl' }}
                              minLength={6}
                              required
                            />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="confirmPassword" className="block text-right" style={{ textAlign: 'right' }}>אישור סיסמה</Label>
                            <Input
                              id="confirmPassword"
                              type="password"
                              value={passwordData.confirmPassword}
                              onChange={(e) => setPasswordData(prev => ({ ...prev, confirmPassword: e.target.value }))}
                              className="text-right [direction:rtl] [text-align:right]"
                              dir="rtl"
                              placeholder="אשר סיסמה חדשה"
                              style={{ textAlign: 'right', direction: 'rtl' }}
                              required
                            />
                          </div>
                        </div>
                        <div className="text-right mt-6">
                          <Button
                            onClick={handlePasswordChange}
                            disabled={passwordLoading || !passwordData.currentPassword || !passwordData.newPassword || !passwordData.confirmPassword}
                            className="text-right"
                          >
                            {passwordLoading ? 'משנה סיסמה...' : 'עדכן סיסמה'}
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Addresses Tab */}
            <TabsContent value="addresses" dir="rtl">
              <AddressBook />
            </TabsContent>

            {/* Favorites Tab */}
            <TabsContent value="favorites" dir="rtl">
              <Card dir="rtl">
                <CardHeader dir="rtl">
                  <CardTitle className="flex items-center gap-2 text-right">
                    <Heart className="h-5 w-5" />
                    המוצרים המועדפים שלי ({favorites.size})
                  </CardTitle>
                </CardHeader>
                <CardContent dir="rtl">
                  {favoritesLoading ? (
                    <div className="text-center py-8">
                      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
                      <p>טוען מוצרים מועדפים...</p>
                    </div>
                  ) : favorites.size === 0 ? (
                    <div className="text-center py-8">
                      <Heart className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                      <p className="text-muted-foreground mb-4">אין מוצרים מועדפים עדיין</p>
                      <Button onClick={() => navigate('/products')} variant="outline">
                        עבור לרשימת המוצרים
                      </Button>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                      {favoriteProducts.map((product) => (
                        <ProductCard key={product.id} product={product} viewMode="grid" />
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Orders Tab */}
            <TabsContent value="orders" dir="rtl">
              <Card dir="rtl">
                <CardHeader dir="rtl">
                  <CardTitle className="flex items-center gap-2 text-right">
                    <Package className="h-5 w-5" />
                    ההזמנות שלי ({orders.length})
                  </CardTitle>
                </CardHeader>
                <CardContent dir="rtl">
                  {orders.length === 0 ? (
                    <div className="text-center py-8">
                      <Package className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                      <p className="text-muted-foreground">אין הזמנות עדיין</p>
                      <Button onClick={() => navigate('/products')} variant="outline" className="mt-4">
                        לחנות המוצרים
                      </Button>
                    </div>
                  ) : (
                    <>
                      {/* Desktop Table View */}
                      <div className="hidden md:block">
                        <Table dir="rtl">
                          <TableHeader>
                            <TableRow>
                              <TableHead className="text-right">מס' הזמנה</TableHead>
                              <TableHead className="text-right">תאריך</TableHead>
                              <TableHead className="text-right">סטטוס</TableHead>
                              <TableHead className="text-right">מוצרים</TableHead>
                              <TableHead className="text-right">סה"כ</TableHead>
                              <TableHead className="text-center w-24">פעולות</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {orders.map((order) => (
                              <TableRow
                                key={order.id}
                                className="cursor-pointer hover:bg-muted/50"
                                onClick={() => handleOrderClick(order)}
                              >
                                <TableCell className="font-medium">
                                  #{formatOrderNumber(order.order_number)}
                                </TableCell>
                                <TableCell>
                                  <div>
                                    <div>{new Date(order.created_at).toLocaleDateString('he-IL')}</div>
                                    <div className="text-xs text-muted-foreground">
                                      {new Date(order.created_at).toLocaleTimeString('he-IL', { hour: '2-digit', minute: '2-digit' })}
                                    </div>
                                  </div>
                                </TableCell>
                                <TableCell>
                                  {order.status && getStatusBadge(order.status)}
                                </TableCell>
                                <TableCell>
                                  {order.sub_orders?.length || 0} פריטים
                                </TableCell>
                                <TableCell>
                                  <div className="font-bold">₪{order.total_amount.toLocaleString()}</div>
                                  {order.total_delivery_cost > 0 && (
                                    <div className="text-xs text-muted-foreground">
                                      כולל ₪{order.total_delivery_cost.toLocaleString()} משלוח
                                    </div>
                                  )}
                                </TableCell>
                                <TableCell className="text-center">
                                  <Button variant="outline" size="sm" onClick={(e) => {
                                    e.stopPropagation();
                                    handleOrderClick(order);
                                  }}>
                                    <Eye className="h-4 w-4 ml-1" />
                                    צפה
                                  </Button>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>

                      {/* Mobile Card View */}
                      <div className="md:hidden space-y-3">
                        {orders.map((order) => (
                          <div
                            key={order.id}
                            className="border rounded-lg p-4 cursor-pointer hover:shadow-md transition-shadow bg-card"
                            onClick={() => handleOrderClick(order)}
                          >
                            <div className="flex justify-between items-start mb-3">
                              <div>
                                <p className="font-bold text-lg">
                                  #{formatOrderNumber(order.order_number)}
                                </p>
                                <p className="text-sm text-muted-foreground">
                                  {new Date(order.created_at).toLocaleDateString('he-IL')}
                                </p>
                              </div>
                              <div className="text-left">
                                <p className="font-bold text-lg">₪{order.total_amount.toLocaleString()}</p>
                                {order.status && getStatusBadge(order.status)}
                              </div>
                            </div>
                            <div className="flex justify-between items-center text-sm text-muted-foreground border-t pt-2">
                              <span>{order.sub_orders?.length || 0} פריטים</span>
                              <div className="flex items-center gap-1">
                                <Truck className="w-4 h-4" />
                                <span>
                                  {order.total_delivery_cost === 0 ? 'איסוף עצמי' : 'משלוח'}
                                </span>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

          </Tabs>

          {/* Order Details Modal */}
          <Dialog open={orderModalOpen} onOpenChange={setOrderModalOpen}>
            <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto text-right" dir="rtl">
              <DialogHeader>
                <DialogTitle className="text-xl font-bold flex items-center gap-3 justify-start" dir="rtl">
                  {selectedOrder?.status && getStatusBadge(selectedOrder.status)}
                  <span>פרטי הזמנה #{selectedOrder && formatOrderNumber(selectedOrder.order_number)}</span>
                  {(profile as any)?.role === 'admin' && (
                    <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200 text-xs">
                      הזמנה טסט
                    </Badge>
                  )}
                </DialogTitle>
              </DialogHeader>

              {selectedOrder && (
                <div className="space-y-6" dir="rtl">
                  {/* Business Details Section - Only show if data exists on the order */}
                  {(selectedOrder.billing_address?.businessName || selectedOrder.billing_address?.taxId || selectedOrder.billing_address?.business_name || selectedOrder.billing_address?.tax_id) && (
                    <div className="bg-muted/30 p-4 rounded-lg border mb-4">
                      <h3 className="font-semibold mb-3 flex items-center gap-2">
                        <Building2 className="w-4 h-4" />
                        פרטי עסק
                      </h3>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                        <div>
                          <span className="text-muted-foreground block text-xs mb-1">שם העסק:</span>
                          <span className="font-medium">
                            {selectedOrder.billing_address?.businessName || selectedOrder.billing_address?.business_name || "---"}
                          </span>
                        </div>
                        <div>
                          <span className="text-muted-foreground block text-xs mb-1">ח.פ / עוסק מורשה:</span>
                          <span className="font-medium">
                            {selectedOrder.billing_address?.taxId || selectedOrder.billing_address?.tax_id || "---"}
                          </span>
                        </div>
                        <div>
                          <span className="text-muted-foreground block text-xs mb-1">כתובת העסק:</span>
                          <span className="font-medium">
                            {selectedOrder.billing_address?.businessAddress || selectedOrder.billing_address?.business_address || "---"}
                          </span>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Order Header with Status */}
                  <div className="flex flex-wrap items-center justify-between gap-4 pb-4 border-b">
                    <div>
                      <p className="text-sm text-muted-foreground">תאריך הזמנה</p>
                      <p className="font-medium">
                        {new Date(selectedOrder.created_at).toLocaleDateString('he-IL')} בשעה{' '}
                        {new Date(selectedOrder.created_at).toLocaleTimeString('he-IL', { hour: '2-digit', minute: '2-digit' })}
                      </p>
                    </div>
                  </div>

                  {/* Shipping/Delivery Method Merged Section */}
                  <div className="bg-muted/50 p-4 rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="font-semibold flex items-center gap-2">
                        {selectedOrder.total_delivery_cost > 0 ? <Truck className="w-4 h-4" /> : <MapPin className="w-4 h-4" />}
                        {selectedOrder.total_delivery_cost > 0 ? 'משלוח לכתובת' : 'איסוף עצמי'}
                      </h3>
                    </div>

                    {selectedOrder.total_delivery_cost > 0 ? (
                      selectedOrder.full_shipping_address ? (
                        <div className="text-sm space-y-1">
                          <p className="font-medium">{selectedOrder.full_shipping_address.recipient_name}</p>
                          <p>
                            {selectedOrder.full_shipping_address.street_name} {selectedOrder.full_shipping_address.street_number}, {selectedOrder.full_shipping_address.city}
                          </p>
                          {selectedOrder.full_shipping_address.details && (
                            <p className="text-muted-foreground">{selectedOrder.full_shipping_address.details}</p>
                          )}
                        </div>
                      ) : (
                        <p className="text-sm text-amber-600">
                          {selectedOrder.shipping_city || 'כתובת לא צוינה'}
                        </p>
                      )
                    ) : (
                      <p className="text-sm font-medium">
                        איסוף עצמי מהמחסן
                      </p>
                    )}
                  </div>

                  {/* Products Section */}
                  <div>
                    <h3 className="font-semibold mb-3">פירוט מוצרים ({selectedOrder.sub_orders?.length || 0})</h3>

                    {/* Desktop Table View */}
                    <div className="hidden md:block border rounded-lg overflow-hidden">
                      <Table>
                        <TableHeader>
                          <TableRow className="bg-muted/50">
                            <TableHead className="text-right">מוצר</TableHead>
                            <TableHead className="text-center w-20">כמות</TableHead>
                            <TableHead className="text-center w-24">מחיר יח'</TableHead>
                            <TableHead className="text-left w-24">סה"כ</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {selectedOrder.sub_orders?.map((item) => {
                            const productImage = item.products?.images?.[item.products.main_image_index || 0];
                            const productSlug = item.products?.slug;
                            const productId = item.product_id;
                            const isClickable = (productSlug || productId);

                            return (
                              <TableRow
                                key={item.id}
                                className={isClickable ? 'cursor-pointer hover:bg-muted/50' : ''}
                                onClick={() => {
                                  if (isClickable) {
                                    setOrderModalOpen(false);
                                    if (productSlug) {
                                      navigate(`/product/${productSlug}`);
                                    } else if (productId) {
                                      navigate(`/product/${productId}`);
                                    }
                                  }
                                }}
                              >
                                <TableCell>
                                  <div className="flex items-center gap-3">
                                    {productImage && (
                                      <div className="w-12 h-12 rounded overflow-hidden bg-muted flex-shrink-0">
                                        <img src={productImage} alt={item.products?.name} className="w-full h-full object-cover" />
                                      </div>
                                    )}
                                    <div>
                                      <p className="font-medium">{item.products?.name}</p>
                                      {isClickable && (
                                        <div className="mt-1">
                                          {item.products?.liquidation_sale_enabled ? (
                                            <Badge className="bg-gradient-to-r from-red-600 to-red-500 text-white border-0 text-[10px] px-2 py-0.5 h-auto">
                                              מכירת חיסול
                                            </Badge>
                                          ) : item.products?.group_buy_enabled ? (
                                            <Badge className="bg-gradient-to-r from-amber-500 to-yellow-400 text-white shadow-sm border-0 text-[10px] px-2 py-0.5 h-auto">
                                              קבוצת רכישה
                                            </Badge>
                                          ) : (
                                            <Badge className="bg-gradient-to-r from-blue-600 to-blue-400 text-white border-0 text-[10px] px-2 py-0.5 h-auto shadow-sm">
                                              מכירה רגילה
                                            </Badge>
                                          )}
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                </TableCell>
                                <TableCell className="text-center">{item.quantity.toLocaleString()}</TableCell>
                                <TableCell className="text-center">₪{item.unit_price.toLocaleString()}</TableCell>
                                <TableCell className="text-left font-bold">₪{item.total_price.toLocaleString()}</TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </div>

                    {/* Mobile Card View */}
                    <div className="md:hidden space-y-3">
                      {selectedOrder.sub_orders?.map((item) => {
                        const productImage = item.products?.images?.[item.products.main_image_index || 0];
                        const productSlug = item.products?.slug;
                        const productId = item.product_id;
                        const isClickable = (productSlug || productId);

                        return (
                          <div
                            key={item.id}
                            className={`border rounded-lg p-3 ${isClickable ? 'cursor-pointer hover:border-primary' : ''}`}
                            onClick={() => {
                              if (isClickable) {
                                setOrderModalOpen(false);
                                if (productSlug) {
                                  navigate(`/product/${productSlug}`);
                                } else if (productId) {
                                  navigate(`/product/${productId}`);
                                }
                              }
                            }}
                          >
                            <div className="flex gap-3">
                              {productImage && (
                                <div className="w-16 h-16 rounded overflow-hidden bg-muted flex-shrink-0">
                                  <img src={productImage} alt={item.products?.name} className="w-full h-full object-cover" />
                                </div>
                              )}
                              <div className="flex-1 min-w-0">
                                <p className="font-medium text-sm truncate">{item.products?.name}</p>
                                <div className="flex justify-between items-center mt-2 text-sm">
                                  <span className="text-muted-foreground">כמות: {item.quantity}</span>
                                  <span className="font-bold">₪{item.total_price.toLocaleString()}</span>
                                </div>
                                {isClickable && (
                                  <div className="mt-2 flex justify-end">
                                    {item.products?.liquidation_sale_enabled ? (
                                      <Badge className="bg-gradient-to-r from-red-600 to-red-500 text-white border-0 text-[10px] px-2 py-0.5 h-auto">
                                        מכירת חיסול
                                      </Badge>
                                    ) : item.products?.group_buy_enabled ? (
                                      <Badge className="bg-gradient-to-r from-amber-500 to-yellow-400 text-white shadow-sm border-0 text-[10px] px-2 py-0.5 h-auto">
                                        קבוצת רכישה
                                      </Badge>
                                    ) : (
                                      <Badge className="bg-gradient-to-r from-blue-600 to-blue-400 text-white border-0 text-[10px] px-2 py-0.5 h-auto shadow-sm">
                                        מכירה רגילה
                                      </Badge>
                                    )}
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Order Totals */}
                  <div className="bg-muted p-4 rounded-lg">
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span>סה"כ מוצרים</span>
                        <span>₪{(selectedOrder.total_amount - selectedOrder.total_delivery_cost).toLocaleString()}</span>
                      </div>
                      {selectedOrder.total_delivery_cost > 0 && (
                        <div className="flex justify-between">
                          <span>עלות משלוח</span>
                          <span>₪{selectedOrder.total_delivery_cost.toLocaleString()}</span>
                        </div>
                      )}
                      <Separator className="my-2" />
                      <div className="flex justify-between text-lg font-bold">
                        <span>סה"כ לתשלום</span>
                        <span className="text-primary">₪{selectedOrder.total_amount.toLocaleString()}</span>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </DialogContent>
          </Dialog>

        </div>
      </div>
      <Footer />
    </>
  );
};

export default Profile;